/**
 * Rich text rules (design 2026-10-31, C1). One pure check used by API and web:
 * the API refuses anything else with 400, the web never renders anything else.
 */
export const RICH_TEXT_LINK_SCHEMES = ['https:', 'http:', 'mailto:'];
/** `doc` is level 1; every nested node, including text, adds one. */
export const RICH_TEXT_MAX_DEPTH = 10;
const INVALID = 'Der Text enthält ein nicht erlaubtes Format.';
const TOO_DEEP = 'Der Text ist zu tief verschachtelt.';
const BAD_LINK = 'Links sind nur mit https:, http: oder mailto: erlaubt.';
/** Scheme, then a host (http/https) or an address (mailto); no whitespace. */
const ALLOWED_HREF = /^(?:https?:\/\/[^\s/?#]+[^\s]*|mailto:[^\s]+)$/i;
/** Link targets allowed in stored text and in every rendering. */
export function isAllowedHref(href) {
    if (typeof href !== 'string' || href.length > 2000 || !ALLOWED_HREF.test(href))
        return false;
    // No control characters: browsers strip some of them, which can turn a
    // harmless looking target into another scheme.
    for (const char of href) {
        const code = char.charCodeAt(0);
        if (code < 0x20 || code === 0x7f)
            return false;
    }
    return true;
}
function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** True when `value` has no keys beyond `allowed`. Shared with section-blocks. */
export function hasOnlyKeys(value, allowed) {
    return Object.keys(value).every((key) => allowed.includes(key));
}
function checkMarks(marks) {
    if (marks === undefined)
        return undefined;
    if (!Array.isArray(marks))
        return INVALID;
    const seen = new Set();
    for (const mark of marks) {
        if (!isObject(mark) || typeof mark.type !== 'string' || seen.has(mark.type))
            return INVALID;
        seen.add(mark.type);
        if (mark.type === 'link') {
            if (!hasOnlyKeys(mark, ['type', 'attrs']) || !isObject(mark.attrs))
                return INVALID;
            if (!hasOnlyKeys(mark.attrs, ['href']))
                return INVALID;
            if (!isAllowedHref(mark.attrs.href))
                return BAD_LINK;
        }
        else if (mark.type === 'bold' || mark.type === 'italic' || mark.type === 'placeholder') {
            if (!hasOnlyKeys(mark, ['type']))
                return INVALID;
        }
        else {
            return INVALID;
        }
    }
    return undefined;
}
function checkInline(node, depth) {
    if (depth > RICH_TEXT_MAX_DEPTH)
        return TOO_DEEP;
    if (!isObject(node))
        return INVALID;
    if (node.type === 'hardBreak')
        return hasOnlyKeys(node, ['type']) ? undefined : INVALID;
    if (node.type !== 'text' || !hasOnlyKeys(node, ['type', 'text', 'marks']))
        return INVALID;
    if (typeof node.text !== 'string' || node.text.length === 0)
        return INVALID;
    return checkMarks(node.marks);
}
function checkList(content, depth, check, rule) {
    if (content === undefined)
        return rule === 'optional' ? undefined : INVALID;
    if (!Array.isArray(content))
        return INVALID;
    if (rule === 'nonEmpty' && content.length === 0)
        return INVALID;
    for (const child of content) {
        const problem = check(child, depth);
        if (problem)
            return problem;
    }
    return undefined;
}
function checkListItem(node, depth) {
    if (depth > RICH_TEXT_MAX_DEPTH)
        return TOO_DEEP;
    if (!isObject(node) || node.type !== 'listItem' || !hasOnlyKeys(node, ['type', 'content'])) {
        return INVALID;
    }
    return checkList(node.content, depth + 1, checkBlock, 'nonEmpty');
}
function checkBlock(node, depth) {
    if (depth > RICH_TEXT_MAX_DEPTH)
        return TOO_DEEP;
    if (!isObject(node))
        return INVALID;
    switch (node.type) {
        case 'paragraph':
            if (!hasOnlyKeys(node, ['type', 'content']))
                return INVALID;
            return checkList(node.content, depth + 1, checkInline, 'optional');
        case 'bulletList':
            if (!hasOnlyKeys(node, ['type', 'content']))
                return INVALID;
            return checkList(node.content, depth + 1, checkListItem, 'nonEmpty');
        case 'orderedList': {
            if (!hasOnlyKeys(node, ['type', 'attrs', 'content']))
                return INVALID;
            if (node.attrs !== undefined) {
                if (!isObject(node.attrs) || !hasOnlyKeys(node.attrs, ['start']))
                    return INVALID;
                const start = node.attrs.start;
                if (start !== undefined && !(Number.isInteger(start) && start >= 0)) {
                    return INVALID;
                }
            }
            return checkList(node.content, depth + 1, checkListItem, 'nonEmpty');
        }
        default:
            return INVALID;
    }
}
/** German reason when `value` is not allowed rich text, `undefined` when it is. */
export function checkRichText(value) {
    if (!isObject(value) || value.type !== 'doc' || !hasOnlyKeys(value, ['type', 'content'])) {
        return INVALID;
    }
    return checkList(value.content, 2, checkBlock, 'array');
}
/** One paragraph with the `placeholder` mark - how templates fill a chapter. */
export function placeholderDocument(text) {
    return {
        type: 'doc',
        content: [
            { type: 'paragraph', content: [{ type: 'text', text, marks: [{ type: 'placeholder' }] }] },
        ],
    };
}
function inlineNodes(nodes) {
    return nodes.flatMap((node) => node.type === 'paragraph'
        ? (node.content ?? [])
        : node.content.flatMap((item) => inlineNodes(item.content)));
}
export function hasPlaceholder(doc) {
    return inlineNodes(doc.content).some((node) => node.type === 'text' && (node.marks ?? []).some((mark) => mark.type === 'placeholder'));
}
/** True when the document holds no visible character. */
export function isRichTextEmpty(doc) {
    return !inlineNodes(doc.content).some((node) => node.type === 'text' && node.text.trim() !== '');
}
/** Plain text, one line per paragraph, list items with "- " - for copying. */
export function richTextPlainText(doc) {
    const lines = [];
    const visit = (nodes, prefix) => {
        for (const node of nodes) {
            if (node.type === 'paragraph') {
                lines.push(prefix +
                    (node.content ?? [])
                        .map((inline) => (inline.type === 'text' ? inline.text : '\n'))
                        .join(''));
            }
            else {
                for (const item of node.content)
                    visit(item.content, node.type === 'bulletList' ? '- ' : '1. ');
            }
        }
    };
    visit(doc.content, '');
    return lines.join('\n');
}
//# sourceMappingURL=rich-text.js.map